import { Injectable, inject } from '@angular/core';
import { Observable, catchError, of, tap, map } from 'rxjs';
import { BaseAsyncService } from '@core/state/base-service';
import { NotaryDto } from '@shared/models/dto/api-dtos';
import { ApiPaginatedResponse } from '@shared/models/notaire/notaire.models';
import { NotariesStore } from '@core/state/notaries.store';

/**
 * Query parameters for the notaries API endpoint
 */
export interface NotariesQueryParams {
  paginated?: boolean;
  page?: number;
  pageSize?: number;
  actif?: boolean | null;
  search?: string;
  notaryOfficeId?: number;
}

/**
 * Service for managing Notaries data
 * Handles API calls to the /api/notaries endpoint with query parameters
 * Supports pagination, filtering, and searching
 * Extends BaseAsyncService for consistent error handling and retry logic
 */
@Injectable({ providedIn: 'root' })
export class NotariesService extends BaseAsyncService {
  private readonly notariesStore = inject(NotariesStore);

  private readonly NOTARIES_ENDPOINT = '/notaries';

  /**
   * Builds query string from parameters
   * @param params Query parameters
   * @returns Query string
   */
  private buildQueryParams(params: NotariesQueryParams): string {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.set(key, String(value));
      }
    });
    
    return queryParams.toString();
  }

  /**
   * Loads notaries with pagination and filtering
   * @param params Query parameters for the API
   * @returns Observable of notaries array
   */
  loadNotaries(params: NotariesQueryParams = {}): Observable<NotaryDto[]> {
    this.notariesStore.setLoading(true);
    this.notariesStore.setError(null);

    // Build query string from parameters
    const queryParams = this.buildQueryParams(params);
    const endpoint = queryParams ? `${this.NOTARIES_ENDPOINT}?${queryParams}` : this.NOTARIES_ENDPOINT;

    return this.get<NotaryDto[] | ApiPaginatedResponse<NotaryDto>>(endpoint, {
      logErrors: true,
      retry: this.createRetryConfig(2, 800, 1.5)
    }).pipe(
      map((response: NotaryDto[] | ApiPaginatedResponse<NotaryDto>) => {
        let notaries: NotaryDto[];
        
        // Check if response is paginated
        if (params.paginated && 'pagination' in response) {
          const paginatedResponse = response as ApiPaginatedResponse<NotaryDto>;
          notaries = paginatedResponse.data;
        } else {
          // Handle non-paginated response
          notaries = response as NotaryDto[];
        }
        
        // Update store with data
        this.notariesStore.setNotaries(notaries);

        return notaries;
      }),
      catchError((error) => {
        this.notariesStore.setError('Impossible de charger les notaires');
        console.error('Error loading notaries:', error);
        return of([]);
      }),
      tap(() => this.notariesStore.setLoading(false))
    );
  }

  /**
   * Loads notaries with server-side pagination
   * @param page Page number
   * @param pageSize Items per page
   * @param filters Additional filters
   * @returns Observable of paginated response
   */
  loadNotariesPaginated(
    page: number = 1, 
    pageSize: number = 20,
    filters: Partial<NotariesQueryParams> = {}
  ): Observable<ApiPaginatedResponse<NotaryDto>> {
    this.notariesStore.setLoading(true);
    this.notariesStore.setError(null);

    const params = { ...filters, page, pageSize, paginated: true };
    const queryParams = this.buildQueryParams(params);
    const endpoint = queryParams ? `${this.NOTARIES_ENDPOINT}?${queryParams}` : this.NOTARIES_ENDPOINT;

    return this.get<ApiPaginatedResponse<NotaryDto>>(endpoint, {
      logErrors: true,
      retry: this.createRetryConfig(2, 800, 1.5)
    }).pipe(
      map((response: ApiPaginatedResponse<NotaryDto>) => {
        // Update store with data
        this.notariesStore.setNotaries(response.data);

        return response;
      }),
      catchError((error) => {
        this.notariesStore.setError('Impossible de charger les notaires');
        console.error('Error loading notaries:', error);
        return of({
          pagination: {
            pageNumber: 1,
            pageSize: 20,
            count: 0,
            countPages: 0,
            hasPreviousPage: false,
            hasNextPage: false
          },
          data: []
        });
      }),
      tap(() => this.notariesStore.setLoading(false))
    );
  }

  /**
   * Searches notaries by term
   * @param searchTerm Search term
   * @param notaryOfficeId Optional notary office ID filter
   * @returns Observable of filtered results
   */
  searchNotaries(searchTerm: string, notaryOfficeId?: number): Observable<NotaryDto[]> {
    return this.loadNotaries({
      search: searchTerm,
      notaryOfficeId
    });
  }

  /**
   * Loads notaries filtered by notary office ID
   * @param notaryOfficeId Notary office ID
   * @param actif Optional active status filter
   * @returns Observable of filtered results
   */
  loadNotariesByOffice(notaryOfficeId: number, actif?: boolean | null): Observable<NotaryDto[]> {
    return this.loadNotaries({
      notaryOfficeId,
      actif
    });
  }

  /**
   * Refreshes the current data
   * @returns Observable of refreshed data
   */
  refresh(): Observable<NotaryDto[]> {
    const currentState = this.notariesStore.state();
    return this.loadNotaries({
      notaryOfficeId: currentState.filters.notaryOfficeId,
      actif: currentState.filters.actif,
      search: currentState.filters.search
    });
  }
}

